import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Modal, notification, Space, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useGetWorksheetsQuery,
  useDeleteWorksheetMutation,
} from '@/state/slices/reports';
import type { Worksheet } from '@/models/template';
import HeartLoader from '@/shared/ui/HeartLoader';
import styles from './WorksheetList.module.css';

const { Text } = Typography;

export function WorksheetList() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetWorksheetsQuery();
  const [deleteWorksheet] = useDeleteWorksheetMutation();

  const worksheets = data?.results ?? [];

  const handleDelete = useCallback(
    (worksheet: Worksheet) => {
      Modal.confirm({
        title: 'Delete Worksheet',
        content: `Are you sure you want to delete "${worksheet.name}"? This action cannot be undone.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteWorksheet(worksheet.uuid).unwrap();
            notification.success({ title: 'Worksheet deleted' });
          } catch {
            notification.error({ title: 'Failed to delete worksheet' });
          }
        },
      });
    },
    [deleteWorksheet]
  );

  const columns: ColumnsType<Worksheet> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (date: string) =>
        new Date(date).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/templates/worksheets/${record.uuid}`)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={worksheets}
        loading={{
          spinning: isLoading,
          indicator: <HeartLoader size="sm" />,
        }}
        pagination={false}
        locale={{ emptyText: 'No worksheets found' }}
      />
    </div>
  );
}
